import { CUSTOM_ELEMENTS_SCHEMA, ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Location, TitleCasePipe } from '@angular/common';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { NoodleQuestionDto } from '@/app/common/general-types/noodle.dto';
import { ScreeningReportPresenceDto, ScreeningResultsResponse } from '@/app/common/general-types/social-screening.dto';
import { executiveSummaryFromReport } from './screening-brief-report.summary';
import { SocialScreeningService } from '@/app/services/social-screening/social-screening.service';
import {
  adaptCreatorContent,
  deriveRuleOutcome,
  summariseCreator,
  visibleScreeningContent,
} from '../manage-brief-tabs-screen/data/screening-results.adapter';
import {
  ContentView,
  EMPTY_PROGRESS,
  RuleAttribution,
  RuleOutcome,
  ScreeningContentItem,
  ScreeningFlag,
} from '../manage-brief-tabs-screen/data/screening-results.model';
import { ScreeningResultsService } from '../manage-brief-tabs-screen/services/screening-results/screening-results.service';
import { ComingSoon } from '../manage-brief-tabs-screen/ui/coming-soon/coming-soon';
import { ContentCard } from './molecules/content-card/content-card';
import { ReportPlaceholder } from '../manage-brief-tabs-screen/ui/report-placeholder/report-placeholder';
import { SeverityPill } from '../manage-brief-tabs-screen/ui/severity-pill/severity-pill';
import { VerdictPill } from '../manage-brief-tabs-screen/ui/verdict-pill/verdict-pill';
import { ScreeningTabDef, ScreeningTabs } from '../manage-brief-tabs-screen/ui/screening-tabs/screening-tabs';
import { MEDIA_COMING_SOON, PLACEHOLDER_SECTIONS, VERDICT_LINE } from './screening-brief-report.copy';

type ReportTab = 'report' | 'content' | 'media';
type PageState = 'loading' | 'ready' | 'creator-missing';

interface ScoredRule {
  rule: NoodleQuestionDto;
  number: number;
  outcome: RuleOutcome;
}

/**
 * The per-creator screening report.
 *
 * Works from a cold deep-link: if the hub hasn't been visited, it loads the brief, its rules and
 * the cohort itself before fetching this creator's results.
 */
@Component({
  selector: 'app-screening-brief-report',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TitleCasePipe, ScreeningTabs, VerdictPill, SeverityPill, ReportPlaceholder, ComingSoon, ContentCard],
  templateUrl: './screening-brief-report.html',
  styleUrl: './screening-brief-report.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ScreeningBriefReportComponent {

  private readonly route = inject(ActivatedRoute);
  private readonly location = inject(Location);
  private readonly destroyRef = inject(DestroyRef);
  private readonly socialScreening = inject(SocialScreeningService);
  readonly results = inject(ScreeningResultsService);

  readonly placeholderSections = PLACEHOLDER_SECTIONS;
  readonly mediaCopy = MEDIA_COMING_SOON;

  readonly briefId = signal<string | undefined>(undefined);
  /** Our creator uid, from the route. */
  readonly creatorUserId = signal<string | undefined>(undefined);
  readonly activeTab = signal<ReportTab>('report');
  /** Content-tab filter. `issues` hides posts that passed every rule. */
  readonly contentFilter = signal<ContentView>('all');

  private readonly creatorResults = signal<ScreeningResultsResponse | undefined>(undefined);
  private readonly resultsLoaded = signal<boolean>(false);
  /** Whether a report row exists for this creator on this brief. Unknown until the check returns. */
  private readonly reportPresence = signal<'unknown' | 'absent' | 'present'>('unknown');
  /** Stored report JSON for this Noodle creator, when the row has been filled in. */
  private readonly screeningReport = signal<ScreeningReportPresenceDto['report']>(null);
  private reportCheckKey: string | undefined;

  /** Written read from the stored report. Empty until `creatorReport.summary` is present. */
  readonly executiveSummary = computed(() => executiveSummaryFromReport(this.screeningReport()));

  readonly generatingReport = signal(false);

  /** The generate button is only offered when we know no report has been requested yet. */
  readonly showGenerateReport = computed(() => this.reportPresence() === 'absent');

  /** The cohort row for this creator, once the inquiries have loaded. */
  readonly creator = computed(() =>
    this.results.creators().find((row) => row.creatorUserId === this.creatorUserId()),
  );

  readonly pageState = computed<PageState>(() => {
    if (this.results.cohortState() === 'loading' || this.results.briefState() === 'loading') {
      return 'loading';
    }
    return this.creator() ? 'ready' : 'creator-missing';
  });

  readonly progress = computed(() =>
    this.creatorResults() ? summariseCreator(this.creatorResults()) : this.creator()?.progress ?? { ...EMPTY_PROGRESS },
  );

  /** Flags Noodle attributed to this creator. Empty when the payload carried counts only. */
  readonly flags = computed<readonly ScreeningFlag[]>(() => {
    const noodleId = this.creator()?.noodleCreatorId;
    return noodleId ? this.results.flagsByCreator().get(noodleId) ?? [] : [];
  });

  readonly attribution = computed<RuleAttribution>(() => (this.flags().length ? 'per-rule' : 'counts-only'));

  readonly content = computed<readonly ScreeningContentItem[]>(() =>
    adaptCreatorContent(this.creatorResults(), this.flags()),
  );

  readonly visibleContent = computed(() =>
    visibleScreeningContent(this.content(), this.contentFilter()),
  );

  /** Posts with at least one issue, for the content filter menu. */
  readonly issueContentCount = computed(() =>
    visibleScreeningContent(this.content(), 'issues').length,
  );

  readonly breachedRuleIds = computed(
    () => new Set(this.flags().map((flag) => flag.rule?.id).filter((id): id is string => !!id)),
  );

  readonly scoredRules = computed<ScoredRule[]>(() => {
    const progress = this.progress();
    const attribution = this.attribution();
    const breached = this.breachedRuleIds();
    return this.results.rules().map((rule, index) => ({
      rule,
      number: index + 1,
      outcome: deriveRuleOutcome({
        rule,
        evaluatedCount: progress.evaluated,
        totalIssues: progress.issues,
        breachedRuleIds: breached,
        attribution,
      }),
    }));
  });

  /** Shown above the scorecard when issues exist but Noodle didn't say which rule they hit. */
  readonly unattributedNote = computed(() => {
    const progress = this.progress();
    if (this.attribution() === 'per-rule' || progress.issues === 0) {
      return undefined;
    }
    return `SHOUT AI found ${progress.issues} issue${progress.issues === 1 ? '' : 's'} across this creator's content but hasn't yet reported which rule each one relates to.`;
  });

  readonly verdictLine = computed(() => {
    const progress = this.progress();
    return VERDICT_LINE[progress.verdict]({
      rulesTotal: this.results.rules().length,
      flaggedItems: progress.flaggedContent,
      evaluated: progress.evaluated,
      total: progress.total,
    });
  });

  readonly breachedCount = computed(() =>
    this.attribution() === 'per-rule'
      ? this.scoredRules().filter((scored) => scored.outcome === 'breached').length
      : this.progress().issues,
  );

  /** Under counts-only we know the issue count, not which rules — so we name what we know. */
  readonly breachedLabel = computed(() =>
    this.attribution() === 'per-rule' ? 'Rules breached' : 'Issues found',
  );

  /** Content totals exist once Noodle reports how many items are in scope. */
  readonly hasContentTotals = computed(() => this.progress().total > 0);

  /** Rule totals exist once the brief's rules have loaded. */
  readonly hasRuleTotals = computed(() => this.results.rules().length > 0);

  readonly statFigureClass =
    'm-0 mt-1.5 font-heading text-3xl font-extrabold leading-none tracking-tight text-foreground';
  readonly statAlertClass =
    'm-0 mt-1.5 font-heading text-3xl font-extrabold leading-none tracking-tight text-red-500 dark:text-red-400';
  readonly statPendingClass =
    'm-0 mt-1.5 font-heading text-3xl font-extrabold leading-none tracking-tight text-muted-foreground';

  readonly breachedStatClass = computed(() => {
    if (!this.hasContentTotals()) {
      return this.statPendingClass;
    }
    return this.breachedCount() > 0 ? this.statAlertClass : this.statFigureClass;
  });

  readonly tabs = computed<ScreeningTabDef[]>(() => [
    { id: 'report', label: 'Report' },
    { id: 'content', label: 'Content', count: this.content().length, countNoun: 'items' },
    // { id: 'media', label: 'Media' },
  ]);

  readonly backLink = computed(() => ['/manage-brief-screen', this.briefId() ?? '', 'creators']);

  constructor() {
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => this.applyParams(params));

    // The Noodle id only exists once the cohort has loaded, so fetch results when it appears.
    effect(() => {
      const noodleId = this.creator()?.noodleCreatorId;
      const briefId = this.briefId();
      if (noodleId && briefId && !this.resultsLoaded()) {
        this.loadResults(briefId, noodleId);
      }
      if (noodleId && briefId) {
        this.loadReportPresence(briefId, noodleId);
      }
    });
  }

  private applyParams(params: ParamMap): void {
    const briefId = params.get('briefId') ?? undefined;
    const creatorUserId = params.get('creatorId') ?? undefined;
    this.briefId.set(briefId);
    this.creatorUserId.set(creatorUserId);
    this.resultsLoaded.set(false);
    this.creatorResults.set(undefined);
    this.reportCheckKey = undefined;
    this.reportPresence.set('unknown');
    this.screeningReport.set(null);
    this.generatingReport.set(false);
    if (briefId) {
      // No-op when the hub already loaded this brief.
      this.results.load(briefId);
    }
  }

  private loadResults(briefId: string, noodleCreatorId: string): void {
    const cached = this.results.resultsFor(noodleCreatorId);
    if (cached) {
      this.creatorResults.set(cached);
      this.resultsLoaded.set(true);
      return;
    }
    this.resultsLoaded.set(true);
    this.socialScreening
      .getScreeningResults(noodleCreatorId, briefId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => this.creatorResults.set(response),
        error: () => this.creatorResults.set(undefined),
      });
  }

  generateReport(): void {
    const briefId = this.briefId();
    const creatorId = this.creator()?.noodleCreatorId;
    if (!briefId || !creatorId || this.generatingReport() || this.reportPresence() !== 'absent') {
      return;
    }
    this.generatingReport.set(true);
    this.socialScreening
      .generateScreeningReport(briefId, creatorId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.generatingReport.set(false);
          this.reportPresence.set('present');
        },
        error: () => this.generatingReport.set(false),
      });
  }

  private loadReportPresence(briefId: string, noodleCreatorId: string): void {
    const key = `${briefId}::${noodleCreatorId}`;
    if (this.reportCheckKey === key) {
      return;
    }
    this.reportCheckKey = key;
    this.reportPresence.set('unknown');
    this.screeningReport.set(null);
    this.socialScreening
      .hasScreeningReport(briefId, noodleCreatorId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (presence) => {
          this.reportPresence.set(presence.generated ? 'present' : 'absent');
          this.screeningReport.set(presence.report ?? null);
        },
        error: () => {
          this.reportPresence.set('absent');
          this.screeningReport.set(null);
        },
      });
  }

  selectTab(tab: string): void {
    this.activeTab.set(tab as ReportTab);
  }

  setContentFilter(view: ContentView): void {
    this.contentFilter.set(view);
  }

  ruleNumberLabel(index: number): string {
    return `#${String(index).padStart(2, '0')}`;
  }

  outcomeLabel(outcome: RuleOutcome): string {
    switch (outcome) {
      case 'met':
        return 'Met';
      case 'breached':
        return 'Breached';
      default:
        return 'Not assessed';
    }
  }

  goBack(): void {
    this.location.back();
  }
}
